import React from 'react';
import { ArrowRight, TrendingUp, DollarSign, Calendar, Users, Gift, CheckCircle, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const InvestmentPackagesPage = () => {
  const packages = [
    {
      name: 'Basic Plan',
      image: '/packages/money-2696228_640.jpg',
      minAmount: '$100',
      maxAmount: '$999',
      roi: '15%',
      duration: '24 Hours',
      referralCommission: '5%',
      instantReward: null,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-gold-600',
      description: 'Perfect for beginners looking to start their investment journey with minimal capital.'
    },
    {
      name: 'Gold Plan',
      image: '/packages/gold-163519_1280.jpg',
      minAmount: '$1,000',
      maxAmount: '$4,999',
      roi: '30%',
      duration: '48 Hours',
      referralCommission: '10%',
      instantReward: null,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-gold-600',
      description: 'Premium investment option with enhanced returns for serious investors.'
    },
    {
      name: 'Master Plan',
      image: '/images/chart-1905224_640.jpg',
      minAmount: '$5,000',
      maxAmount: '$9,999',
      roi: '45%',
      duration: '72 Hours',
      referralCommission: '15%',
      instantReward: null,
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-500',
      description: 'Advanced investment package designed for experienced investors seeking maximum returns.'
    },
    {
      name: 'Premium Plan',
      image: '/images/sa-rapita-2483668_640.jpg',
      minAmount: '$10,000',
      maxAmount: '$49,999',
      roi: '60%',
      duration: '96 Hours',
      referralCommission: '20%',
      instantReward: null,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-gold-600',
      description: 'Exclusive premium investment opportunity with exceptional returns for high-value investors.'
    },
    {
      name: 'VIP Plan',
      image: '/packages/money-2696229_640.jpg',
      minAmount: '$50,000',
      maxAmount: 'Unlimited',
      roi: '75%',
      duration: '120 Hours',
      referralCommission: '25%',
      instantReward: null,
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-500',
      description: 'Ultimate VIP investment package with the highest returns and unlimited investment potential.'
    }
  ];

  const benefits = [
    {
      icon: CheckCircle,
      title: 'Secure Investments',
      description: 'All investments are backed by our comprehensive risk management strategies.'
    },
    {
      icon: TrendingUp,
      title: 'Competitive Returns',
      description: 'Enjoy industry-leading returns on your investments with transparent fee structures.'
    },
    {
      icon: Shield,
      title: 'Expert Management',
      description: 'Your investments are managed by experienced financial professionals.'
    },
    {
      icon: Users,
      title: 'Referral Rewards',
      description: 'Earn additional income through our generous referral commission program.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden bg-gradient-to-r from-navy-900 to-navy-800">
        <div className="absolute inset-0 bg-navy-900/40"></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-6xl font-bold font-serif mb-4">Investment Packages</h1>
          <p className="text-xl md:text-2xl text-gray-200">
            Home / <span className="text-gold-400">Investment Packages</span>
          </p>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-6">
              Choose Your <span className="text-gold-600">Investment Plan</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Select from our diverse range of investment packages, each designed to meet different financial goals and risk preferences.
            </p>
          </div>

          {/* Packages Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {packages.map((pkg, index) => (
              <div
                key={pkg.name}
                className={`group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 ${pkg.borderColor} overflow-hidden`}
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={pkg.image}
                    alt={pkg.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-bold text-white drop-shadow-lg">{pkg.name}</h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-gray-600 mb-6 text-sm leading-relaxed">{pkg.description}</p>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-gray-600">
                        <DollarSign className="h-4 w-4 mr-2" />
                        <span className="text-sm">Min Amount:</span>
                      </div>
                      <span className="font-semibold text-navy-900">{pkg.minAmount}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-gray-600">
                        <DollarSign className="h-4 w-4 mr-2" />
                        <span className="text-sm">Max Amount:</span>
                      </div>
                      <span className="font-semibold text-navy-900">{pkg.maxAmount}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-gray-600">
                        <TrendingUp className="h-4 w-4 mr-2" />
                        <span className="text-sm">ROI:</span>
                      </div>
                      <span className={`font-bold ${pkg.textColor}`}>{pkg.roi}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-gray-600">
                        <Calendar className="h-4 w-4 mr-2" />
                        <span className="text-sm">Duration:</span>
                      </div>
                      <span className="font-semibold text-navy-900">{pkg.duration}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-gray-600">
                        <Users className="h-4 w-4 mr-2" />
                        <span className="text-sm">Referral Commission:</span>
                      </div>
                      <span className="font-semibold text-navy-900">{pkg.referralCommission}</span>
                    </div>

                    {pkg.instantReward && (
                      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                        <div className="flex items-center text-gray-600">
                          <Gift className="h-4 w-4 mr-2" />
                          <span className="text-sm">Instant Reward:</span>
                        </div>
                        <span className={`font-bold ${pkg.textColor}`}>{pkg.instantReward}</span>
                      </div>
                    )}
                  </div>

                  {/* CTA */}
                  <a
                    href="https://online.amdconsults.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`group/btn w-full inline-flex items-center justify-center px-6 py-3 ${pkg.bgColor} ${pkg.textColor} font-semibold rounded-lg hover:shadow-md transition-all duration-300`}
                  >
                    Invest Now
                    <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Benefits Section */}
          <div className="bg-gradient-to-br from-navy-50 to-blue-50 rounded-2xl p-12 mb-16">
            <div className="text-center mb-12" data-aos="fade-up">
              <h3 className="text-3xl md:text-4xl font-bold font-serif text-navy-900 mb-4">
                Why Choose Our Investment Packages?
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div key={index} className="text-center" data-aos="fade-up" data-aos-delay={index * 100}>
                    <div className="inline-flex p-4 bg-gold-600 rounded-xl mb-4">
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-navy-900 mb-2">{benefit.title}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-12 text-center text-white">
            <h3 className="text-3xl md:text-4xl font-bold font-serif mb-6">
              Ready to Start Investing?
            </h3>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Choose the investment package that best suits your financial goals and start building your wealth today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://online.amdconsults.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-gold-600 hover:bg-gold-700 text-white font-bold rounded-full transition-colors duration-300"
              >
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <Link 
                to="/services"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-navy-900 font-bold rounded-full transition-all duration-300"
              >
                Learn More
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InvestmentPackagesPage;

